import { invariant } from '../util/invariant';
import type { ProgramConfigElement, SingleChannelElement, ChannelPairElement, LfeChannelElement } from './types';

/**
 * Each channel element has a unique channel index from 0 to nChannels-1
 * that corresponds to the order in which it appears in the raw data block.
 * This function maps these channel index to the layout index to be used when
 * outputing the PCM samples.
 *
 * For instance, we may have:
 *
 *   SCE #0 -> channel index 0
 *   CPE #0 -> channel indexes 1 and 2
 *
 * with a channel layout that tells us that the CPE channels should be first. In
 * that case, the function would return the array [1, 2, 0] indicating that:
 *
 *   t[0] = 1 -> channel index 1 goes to position 0
 *   t[1] = 2 -> channel index 2 goes to position 1
 *   t[2] = 0 -> channel index 0 goes to position 2
 *
 * and the layout in the PCM output would be:
 *
 *   CPE #0 left channel
 *   CPE #0 right channel
 *   SCE #0
 */
export function getChannelLayoutMapping(
  channelConfiguration: number,
  pceElement: ProgramConfigElement | undefined,
  sceElements: SingleChannelElement[],
  cpeElements: ChannelPairElement[],
  lfeElements: LfeChannelElement[],
): number[] {

  switch (channelConfiguration) {
    case 0: {
      if (pceElement === undefined) {
        return getImplicitChannelLayoutMapping(sceElements, cpeElements, lfeElements);
      } else {
        return getExplicitChannelLayoutMapping(pceElement, sceElements, cpeElements, lfeElements);
      }
    }

    // channelConfiguration > 0 indicates one of the default layouts described in the AAC standard

    case 1: {
      // mono
      // SCE: center front speaker
      return [0];
    }

    case 2: {
      // stereo
      // CPE: left, right front speakers
      return [0, 1];
    }

    case 3: {
      // SCE: center front speaker
      // CPE: left, right front speakers
      return [
        cpeElements[0]!.channel0.channelIndex,
        cpeElements[0]!.channel1.channelIndex,
        sceElements[0]!.channel0.channelIndex,
      ];
    }

    case 4: {
      // SCE: center front speaker
      // CPE: left, right center front speakers
      // SCE: rear surround
      return [
        cpeElements[0]!.channel0.channelIndex,
        cpeElements[0]!.channel1.channelIndex,
        sceElements[0]!.channel0.channelIndex,
        sceElements[1]!.channel0.channelIndex,
      ];
    }

    case 5: {
      // SCE: center front speaker
      // CPE: left, right front speakers
      // CPE: left surround, right surround rear speakers
      return [
        cpeElements[0]!.channel0.channelIndex,
        cpeElements[0]!.channel1.channelIndex,
        sceElements[0]!.channel0.channelIndex,
        cpeElements[1]!.channel0.channelIndex,
        cpeElements[1]!.channel1.channelIndex,
      ];
    }

    case 6: {
      // SCE: center front speaker
      // CPE: left, right front speakers
      // CPE: left surround, right surround rear speakers
      // LFE: front low frequency effects speaker
      return [
        cpeElements[0]!.channel0.channelIndex,
        cpeElements[0]!.channel1.channelIndex,
        sceElements[0]!.channel0.channelIndex,
        lfeElements[0]!.channel0.channelIndex,
        cpeElements[1]!.channel0.channelIndex,
        cpeElements[1]!.channel1.channelIndex,
      ];
    }

    case 7: {
      // SCE: center front speaker
      // CPE: left, right center front speakers
      // CPE: left, right outside front speakers
      // CPE: left surround, right surround rear speakers
      // LFE: front low frequency effects speaker
      return [
        cpeElements[0]!.channel0.channelIndex,
        cpeElements[0]!.channel1.channelIndex,
        sceElements[0]!.channel0.channelIndex,
        lfeElements[0]!.channel0.channelIndex,
        cpeElements[2]!.channel0.channelIndex,
        cpeElements[2]!.channel1.channelIndex,
        cpeElements[1]!.channel0.channelIndex,
        cpeElements[1]!.channel1.channelIndex,
      ];
    }
    default: throw `Invalid channel configuration: ${channelConfiguration}`;
  }
}


/**
 * The implicit channel layout mapping corresponds to the order in which the
 * channel elements appeared in the raw data block.
 */
function getImplicitChannelLayoutMapping(
  sceElements: SingleChannelElement[],
  cpeElements: ChannelPairElement[],
  lfeElements: LfeChannelElement[],
): number[] {
  const mapping: number[] = [];
  for (const sce of sceElements) {
    mapping[sce.channel0.channelIndex] = sce.channel0.channelIndex;
  }
  for (const cpe of cpeElements) {
    mapping[cpe.channel0.channelIndex] = cpe.channel0.channelIndex;
    mapping[cpe.channel1.channelIndex] = cpe.channel1.channelIndex;
  }
  for (const lfe of lfeElements) {
    mapping[lfe.channel0.channelIndex] = lfe.channel0.channelIndex;
  }
  return mapping;
}


function getExplicitChannelLayoutMapping(
  pceElement: ProgramConfigElement,
  sceElements: SingleChannelElement[],
  cpeElements: ChannelPairElement[],
  lfeElements: LfeChannelElement[],
): number[] {
  const mapping: number[] = [];

  // Front channels first
  for (let i = 0; i < pceElement.front_element_is_cpe.length; i++) {
    const tag = pceElement.front_element_tag_select[i];
    if (pceElement.front_element_is_cpe[i]) {
      const element = cpeElements.find(e => e.element_instance_tag === tag);
      invariant(element !== undefined, `Could not find CPE element with tag ${tag}`);
      mapping.push(element.channel0.channelIndex);
      mapping.push(element.channel1.channelIndex);
    } else {
      const element = sceElements.find(e => e.element_instance_tag === tag);
      invariant(element !== undefined, `Could not find SCE element with tag ${tag}`);
      mapping.push(element.channel0.channelIndex);
    }
  }

  // Side channels second
  for (let i = 0; i < pceElement.side_element_is_cpe.length; i++) {
    const tag = pceElement.side_element_tag_select[i];
    if (pceElement.side_element_is_cpe[i]) {
      const element = cpeElements.find(e => e.element_instance_tag === tag);
      invariant(element !== undefined, `Could not find CPE element with tag ${tag}`);
      mapping.push(element.channel0.channelIndex);
      mapping.push(element.channel1.channelIndex);
    } else {
      const element = sceElements.find(e => e.element_instance_tag === tag);
      invariant(element !== undefined, `Could not find SCE element with tag ${tag}`);
      mapping.push(element.channel0.channelIndex);
    }
  }

  // Back channels third
  for (let i = 0; i < pceElement.back_element_is_cpe.length; i++) {
    const tag = pceElement.back_element_tag_select[i];
    if (pceElement.back_element_is_cpe[i]) {
      const element = cpeElements.find(e => e.element_instance_tag === tag);
      invariant(element !== undefined, `Could not find CPE element with tag ${tag}`);
      mapping.push(element.channel0.channelIndex);
      mapping.push(element.channel1.channelIndex);
    } else {
      const element = sceElements.find(e => e.element_instance_tag === tag);
      invariant(element !== undefined, `Could not find SCE element with tag ${tag}`);
      mapping.push(element.channel0.channelIndex);
    }
  }

  // LFE channels fourth
  for (let i = 0; i < pceElement.lfe_element_tag_select.length; i++) {
    const tag = pceElement.lfe_element_tag_select[i];
    const element = lfeElements.find(e => e.element_instance_tag === tag);
    invariant(element !== undefined, `Could not find LFE element with tag ${tag}`);
    mapping.push(element.channel0.channelIndex);
  }

  return mapping;
}
